import React from 'react';
import { cn } from '~/lib/utils';
import styles from './ArtifactReference.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ArtifactReferencePin {
  id: string;
  /** 1-based, and the same number the pin wore on the live artifact. */
  n: number;
  kind: 'point' | 'area';
  /** Normalised 0–1 in the artifact's own coordinate space. */
  x: number;
  y: number;
  /** What the note says. Shown on hover, and in the pin's accessible name. */
  note: string;
}

export interface ArtifactReferenceProps {
  /** The artifact's thumbnail. Absent leaves the placeholder ground, which is still a valid reference. */
  src?: string;
  /**
   * Names the artifact for a screen reader — "Artifact 02", not "image".
   *
   * Defaults to `''`, which marks the picture decorative, on `RegionReference`'s precedent. The
   * default is weaker here than it is there, though, and deliberately so: `RegionReference` has a
   * figcaption stating in words what its crop states in pixels, and this block has none — the
   * ruling is that the picture IS the reference. So the only text a screen reader gets is the pins'
   * own names, and a caller who knows which artifact this is should say so.
   */
  alt?: string;
  width?: number;
  height?: number;
  pins: ArtifactReferencePin[];
  theme?: ChatTheme;
  className?: string;
}

/**
 * The artifact a user pointed at, with their numbered pins drawn back onto it.
 *
 * The designer's ruling (2026-09-05): **the picture is the reference**. No citation row, no
 * per-note list — the sibling `RegionReference` gets a `<figcaption>` because one crop needs one
 * sentence to say what it is, and five notes would need five, which is a list of the message the
 * user is about to send written twice. A note's text is reachable by pointing at its pin, and by a
 * screen reader through the pin's accessible name. Nothing else in the turn repeats it.
 *
 * Right-aligned, and no trailing margin, both for `RegionReference`'s reasons: this belongs to the
 * user's turn, and the distance to the bubble under it is the conversation column's to own (see
 * `UserMessage`'s `attachments` slot, which zeroes whatever a child sets and applies its own 8px).
 */
export function ArtifactReference({
  src,
  alt,
  // 190 is `RegionReference`'s media width, kept so two reference blocks in one thread line up on
  // the same right rail; 107 is 16:9 at that width, which is what an artifact actually is.
  width = 190,
  height = 107,
  pins,
  theme: themeProp,
  className,
}: ArtifactReferenceProps) {
  const theme = useV15Theme(themeProp);
  return (
    <figure className={cn(chatThemeClass(theme), styles.root, className)}>
      {/*
        The frame exists only so the tooltips have somewhere to escape to.

        The media has to clip (`overflow: clip`) or a pin near a corner paints square over the 8px
        radius, and a clip cannot be selective — it would take the tooltip with it. So the picture
        keeps its clip and the pins are lifted OUT of it, into an unclipped box that shrink-wraps
        the media and therefore shares its exact coordinate space. That is what makes the tooltip
        the figure's, not the media's, in markup as well as in the comment.
      */}
      <div
        className={styles.frame}
        // The only runtime values in the block, so the only thing passed inline — as custom
        // properties the stylesheet reads, never as direct styling. They inherit to `.media`, which
        // is what the numbers actually size.
        style={
          {
            '--cv15-artifact-w': `${width}px`,
            '--cv15-artifact-h': `${height}px`,
          } as React.CSSProperties
        }
      >
        <div className={styles.media}>
          {src ? <img className={styles.image} src={src} alt={alt ?? ''} /> : null}
        </div>

        {pins.map((pin) => (
          // A `<button>` that does nothing when pressed, which is the point: hover is a pointer-only
          // affordance, and the note behind it has to be reachable without one. A button is the only
          // element that is focusable, announceable and named for free, so Tab reaches the pin, the
          // ring and the tooltip both come up on `:focus-visible`, and a screen reader reads the
          // note off the name without any of that. `cursor: default` in the stylesheet keeps the
          // promise honest — there is nothing to click.
          <button
            key={pin.id}
            type="button"
            className={cn(styles.pin, pin.kind === 'area' ? styles.area : styles.point)}
            // The number is what the pin wore on the live artifact, so it leads the name: a user
            // who is being read "3" by the agent needs to find "3" here.
            aria-label={`Note ${pin.n}: ${pin.note}`}
            style={
              {
                // Unitless, and multiplied by 100% in the stylesheet. Normalised rather than px so
                // the caller never has to know what size this block chose to draw the artifact at —
                // a pin dropped at 0.42 of the way across lands there at 190px and at 380px alike.
                // Stringified because React only skips unit-appending for custom properties, and
                // leaning on that for a raw number is a footgun one refactor away.
                '--cv15-pin-x': String(pin.x),
                '--cv15-pin-y': String(pin.y),
              } as React.CSSProperties
            }
          >
            {pin.n}
            {/* Hidden from assistive tech: `aria-label` above already carries this exact string, and
                announcing both would read the note twice. Same call `RegionReference` makes on its
                crop rectangle — the tooltip is the pointer's route to text that is already there. */}
            <span className={styles.tip} aria-hidden="true">
              {pin.note}
            </span>
          </button>
        ))}
      </div>
    </figure>
  );
}
